import React, { useState } from 'react';
import { X, UserPlus, Mail, KeyRound, UserCheck, Fingerprint, Pencil, Check, ShieldCheck, ShieldAlert } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import * as accountApi from '../../utils/accountApi';
import { useAuth } from '../../context/AuthContext';

const ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer'];

export function AccountMembersSection() {
    const { members, loading, updateRole, updateMember, removeMember, inviteMember, loadMembers } = useOrg();
    const { user: currentUser } = useAuth();
    const [editingId, setEditingId] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [profileEdit, setProfileEdit] = useState(null); // { userId, name, email }
    const [savingProfile, setSavingProfile] = useState(false);
    const [editError, setEditError] = useState('');
    const [showInvite, setShowInvite] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviting, setInviting] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState('');

    const handleRoleChange = async (userId, newRole) => {
        try {
            await updateRole(userId, newRole);
            setEditingId(null);
        } catch {
            // Error displayed by OrgContext
        }
    };

    const startProfileEdit = (member) => {
        setEditError('');
        setConfirmRemove(null);
        setEditingId(null);
        setProfileEdit({ userId: member.user_id, name: member.name || '', email: member.email || '' });
    };

    const cancelProfileEdit = () => {
        setProfileEdit(null);
        setEditError('');
    };

    const saveProfileEdit = async () => {
        if (!profileEdit) return;
        const member = members.find(m => m.user_id === profileEdit.userId);
        const name = profileEdit.name.trim();
        const email = profileEdit.email.trim();
        if (!email) {
            setEditError('Email is required.');
            return;
        }
        // Send only the fields that actually changed.
        const updates = {};
        if (name !== (member?.name || '')) updates.name = name;
        if (email.toLowerCase() !== (member?.email || '').toLowerCase()) updates.email = email;
        if (Object.keys(updates).length === 0) {
            cancelProfileEdit();
            return;
        }
        setSavingProfile(true);
        setEditError('');
        try {
            await updateMember(profileEdit.userId, updates);
            setProfileEdit(null);
        } catch (err) {
            setEditError(err.message || 'Could not save changes.');
        } finally {
            setSavingProfile(false);
        }
    };

    const onProfileEditKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveProfileEdit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelProfileEdit();
        }
    };

    const handleRemove = async (userId) => {
        try {
            await removeMember(userId);
            setConfirmRemove(null);
        } catch {
            // Error displayed by OrgContext
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;
        setInviting(true);
        setInviteSuccess('');
        try {
            await inviteMember(inviteEmail.trim(), inviteRole, inviteName.trim() || undefined);
            setInviteSuccess(`Invited ${inviteName.trim() || inviteEmail.trim()}`);
            setInviteEmail('');
            setInviteName('');
            setInviteRole('member');
            setTimeout(() => setInviteSuccess(''), 4000);
        } catch {
            // Error displayed by OrgContext
        } finally {
            setInviting(false);
        }
    };

    const readPrefs = (member) => {
        if (!member?.preferences) return {};
        try {
            return typeof member.preferences === 'string'
                ? JSON.parse(member.preferences)
                : member.preferences;
        } catch {
            return {};
        }
    };

    const isPasskeyRequired = (member) => !!readPrefs(member).passkey_required;
    const isMfaRequired = (member) => !!readPrefs(member).mfa_required;

    const handleTogglePasskey = async (member) => {
        const prefs = readPrefs(member);
        try {
            await accountApi.updateUserPreferences(member.user_id, { ...prefs, passkey_required: !prefs.passkey_required });
            await loadMembers();
        } catch (err) {
            // Error will be shown by OrgContext
        }
    };

    // Nobody can register a second factor on someone else's behalf — the secret
    // has to reach that person's own phone. What an admin can do is set the
    // flag that stops them at their next sign-in until they have registered one.
    const handleToggleMfaRequired = async (member) => {
        const prefs = readPrefs(member);
        try {
            await accountApi.updateUserPreferences(member.user_id, { ...prefs, mfa_required: !prefs.mfa_required });
            await loadMembers();
        } catch (err) {
            // Error will be shown by OrgContext
        }
    };

    // mfa_registered comes from Hanko itself. Undefined means the question could
    // not be answered, which is shown as unknown — never as "no second factor",
    // so an identity-store outage cannot be misread as an exposed account.
    const mfaState = (member) => {
        if (member.mfa_registered === true) return 'on';
        if (member.mfa_registered === false) return 'off';
        return 'unknown';
    };

    const canManageMember = (member) =>
        member.role !== 'owner' && member.user_id !== currentUser?.user_id;

    return (
        <div>
            <div style={styles.header}>
                <h2 style={styles.title}>Team Members</h2>
                <p style={styles.subtitle}>Manage who has access to this organization and their roles.</p>
            </div>

            <div style={styles.card}>
                {/* Header row with count and invite button */}
                <div style={styles.countRow}>
                    <span style={styles.countLabel}>
                        {members.length} member{members.length !== 1 ? 's' : ''}
                    </span>
                    <button
                        onClick={() => setShowInvite(!showInvite)}
                        style={styles.inviteBtn}
                    >
                        <UserPlus size={14} />
                        Invite Member
                    </button>
                </div>

                {/* Inline invite form */}
                {showInvite && (
                    <form onSubmit={handleInvite} style={styles.inviteForm}>
                        <input
                            type="text"
                            placeholder="Full name"
                            value={inviteName}
                            onChange={e => setInviteName(e.target.value)}
                            style={{ ...styles.inviteInput, flex: '0 0 160px' }}
                        />
                        <input
                            type="email"
                            placeholder="email@example.com"
                            value={inviteEmail}
                            onChange={e => setInviteEmail(e.target.value)}
                            required
                            style={styles.inviteInput}
                            autoFocus
                        />
                        <select
                            value={inviteRole}
                            onChange={e => setInviteRole(e.target.value)}
                            style={styles.inviteRoleSelect}
                        >
                            {ROLE_OPTIONS.filter(r => r !== 'owner').map(r => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                        <button
                            type="submit"
                            disabled={inviting || !inviteEmail.trim()}
                            style={{
                                ...styles.inviteSendBtn,
                                opacity: inviting ? 0.5 : 1,
                            }}
                        >
                            {inviting ? 'Sending…' : 'Send Invite'}
                        </button>
                    </form>
                )}
                {inviteSuccess && (
                    <div style={styles.inviteSuccessMsg}>✓ {inviteSuccess}</div>
                )}

                {/* Members table */}
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Member</th>
                                <th style={{ ...styles.th, width: 100 }}>Status</th>
                                <th style={{ ...styles.th, width: 140 }}>Role</th>
                                <th style={{ ...styles.th, width: 80 }}>Passkey</th>
                                <th style={{ ...styles.th, width: 150 }}>2FA</th>
                                <th style={{ ...styles.th, width: 120 }}>Joined</th>
                                <th style={{ ...styles.th, width: 160, textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(member => (
                                <tr key={member.user_id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.memberInfo}>
                                            <div style={styles.avatar}>
                                                {(member.name || member.email || '?')[0].toUpperCase()}
                                            </div>
                                            {profileEdit?.userId === member.user_id ? (
                                                <div style={styles.editFields}>
                                                    <input
                                                        type="text"
                                                        value={profileEdit.name}
                                                        onChange={e => setProfileEdit(p => ({ ...p, name: e.target.value }))}
                                                        onKeyDown={onProfileEditKeyDown}
                                                        placeholder="Full name"
                                                        style={styles.editInput}
                                                        autoFocus
                                                    />
                                                    <input
                                                        type="email"
                                                        value={profileEdit.email}
                                                        onChange={e => setProfileEdit(p => ({ ...p, email: e.target.value }))}
                                                        onKeyDown={onProfileEditKeyDown}
                                                        placeholder="email@example.com"
                                                        style={styles.editInput}
                                                    />
                                                    {editError && <div style={styles.editErrorMsg}>{editError}</div>}
                                                </div>
                                            ) : (
                                                <div>
                                                    <div style={styles.memberName}>
                                                        {member.name || 'Unnamed'}
                                                    </div>
                                                    <div style={styles.memberEmail}>{member.email}</div>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        {member.status === 'invited' && (
                                            <span style={styles.statusInvited}>
                                                <Mail size={12} /> invited
                                            </span>
                                        )}
                                        {member.status === 'accepted' && (
                                            <span style={styles.statusAccepted}>
                                                <KeyRound size={12} /> accepted
                                            </span>
                                        )}
                                        {(member.status === 'active' || !member.status) && (
                                            <span style={styles.statusActive}>
                                                <UserCheck size={12} />
                                            </span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        {editingId === member.user_id ? (
                                            <select
                                                value={member.role}
                                                onChange={e => handleRoleChange(member.user_id, e.target.value)}
                                                onBlur={() => setEditingId(null)}
                                                autoFocus
                                                style={styles.roleSelect}
                                            >
                                                {ROLE_OPTIONS.map(r => (
                                                    <option key={r} value={r}>{r}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span
                                                style={{
                                                    ...styles.roleBadge,
                                                    ...(member.role === 'owner' || member.user_id === currentUser?.user_id ? styles.roleBadgeOwner : {}),
                                                }}
                                                onClick={() => member.role !== 'owner' && member.user_id !== currentUser?.user_id && setEditingId(member.user_id)}
                                                title={member.user_id === currentUser?.user_id ? 'You' : (member.role !== 'owner' ? 'Click to change role' : '')}
                                            >
                                                {member.role}
                                            </span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        {member.role !== 'owner' && member.user_id !== currentUser?.user_id ? (
                                            <button
                                                onClick={() => handleTogglePasskey(member)}
                                                style={{
                                                    ...styles.removeBtn,
                                                    borderColor: isPasskeyRequired(member) ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.1)',
                                                    color: isPasskeyRequired(member) ? '#22C55E' : '#94A3B8',
                                                }}
                                                title={isPasskeyRequired(member) ? 'Passkey enabled (click to disable)' : 'Enable passkey login'}
                                            >
                                                <Fingerprint size={14} />
                                            </button>
                                        ) : (
                                            <span style={{ color: '#B4C0D3', fontSize: 12 }}>—</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.mfaCell}>
                                            {mfaState(member) === 'on' && (
                                                <span style={styles.mfaOn} title="An authenticator app is registered for this member.">
                                                    <ShieldCheck size={12} /> registered
                                                </span>
                                            )}
                                            {mfaState(member) === 'off' && (
                                                <span style={styles.mfaOff} title="No authenticator app is registered. A password alone signs this member in.">
                                                    <ShieldAlert size={12} /> none
                                                </span>
                                            )}
                                            {mfaState(member) === 'unknown' && (
                                                <span style={styles.mfaUnknown} title="Could not read this member's two-factor status from the sign-in service.">
                                                    unknown
                                                </span>
                                            )}
                                            {canManageMember(member) && (
                                                <button
                                                    onClick={() => handleToggleMfaRequired(member)}
                                                    style={{
                                                        ...styles.requireBtn,
                                                        ...(isMfaRequired(member) ? styles.requireBtnOn : {}),
                                                    }}
                                                    title={isMfaRequired(member)
                                                        ? 'Required. This member must register 2FA before reaching the console. Click to stop requiring it.'
                                                        : 'Stop this member at their next sign-in until they register 2FA.'}
                                                >
                                                    {isMfaRequired(member) ? 'Required' : 'Require'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.dateText}>
                                            {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '—'}
                                        </span>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        {profileEdit?.userId === member.user_id ? (
                                            <div style={styles.confirmRow}>
                                                <button
                                                    onClick={saveProfileEdit}
                                                    disabled={savingProfile}
                                                    style={{ ...styles.saveBtn, opacity: savingProfile ? 0.6 : 1 }}
                                                    title="Save changes"
                                                >
                                                    <Check size={13} /> {savingProfile ? 'Saving…' : 'Save'}
                                                </button>
                                                <button
                                                    onClick={cancelProfileEdit}
                                                    disabled={savingProfile}
                                                    style={styles.confirmNo}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : confirmRemove === member.user_id ? (
                                            <div style={styles.confirmRow}>
                                                <button
                                                    onClick={() => handleRemove(member.user_id)}
                                                    style={styles.confirmYes}
                                                >
                                                    Remove
                                                </button>
                                                <button
                                                    onClick={() => setConfirmRemove(null)}
                                                    style={styles.confirmNo}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={styles.actionRow}>
                                                <button
                                                    onClick={() => startProfileEdit(member)}
                                                    disabled={loading}
                                                    style={styles.editBtn}
                                                    title="Edit name & email"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                {member.role !== 'owner' && member.user_id !== currentUser?.user_id && (
                                                    <button
                                                        onClick={() => setConfirmRemove(member.user_id)}
                                                        disabled={loading}
                                                        style={styles.removeBtn}
                                                        title="Remove member"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {members.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#B4C0D3' }}>
                                        No members found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const styles = {
    header: { marginBottom: 28 },
    title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 700,
        color: '#F1F5F9',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        margin: '6px 0 0',
        fontSize: 14,
        color: '#B4C0D3',
    },
    card: {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12,
        padding: '20px 24px',
    },
    countRow: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16,
    },
    countLabel: {
        fontSize: 13,
        color: '#B4C0D3',
        fontWeight: 500,
    },
    inviteBtn: {
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 14px', background: 'rgba(59,130,246,0.12)',
        border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6,
        color: '#60A5FA', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.15s',
    },
    inviteForm: {
        display: 'flex', gap: 8, alignItems: 'center',
        marginBottom: 16, padding: '12px 0',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    inviteInput: {
        flex: 1, padding: '8px 12px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
        color: '#F1F5F9', fontSize: 13, outline: 'none',
    },
    inviteRoleSelect: {
        padding: '8px 10px', background: '#1E293B',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
        color: '#E2E8F0', fontSize: 13, outline: 'none',
    },
    inviteSendBtn: {
        padding: '8px 16px', background: '#3B82F6',
        border: 'none', borderRadius: 6,
        color: '#fff', fontSize: 13, fontWeight: 600,
        cursor: 'pointer', whiteSpace: 'nowrap',
    },
    inviteSuccessMsg: {
        fontSize: 13, color: '#22C55E', fontWeight: 500,
        marginBottom: 12,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        fontWeight: 700,
        color: '#A3B1C6',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    tr: {
        borderBottom: '1px solid rgba(255,255,255,0.04)',
    },
    td: {
        padding: '12px 12px',
        verticalAlign: 'middle',
    },
    memberInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    avatar: {
        width: 34,
        height: 34,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
    },
    memberName: {
        fontSize: 14,
        fontWeight: 600,
        color: '#E2E8F0',
    },
    memberEmail: {
        fontSize: 13,
        color: '#B4C0D3',
        marginTop: 1,
    },
    roleBadge: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        color: '#CBD5E1',
        background: 'rgba(255,255,255,0.05)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    roleBadgeOwner: {
        color: '#FBBF24',
        background: 'rgba(251, 191, 36, 0.1)',
        cursor: 'default',
    },
    roleSelect: {
        padding: '4px 8px',
        background: '#1E293B',
        border: '1px solid rgba(59,130,246,0.4)',
        borderRadius: 6,
        color: '#E2E8F0',
        fontSize: 12,
        outline: 'none',
    },
    dateText: {
        fontSize: 13,
        color: '#B4C0D3',
    },
    mfaCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    mfaOn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: '#4ADE80',
        whiteSpace: 'nowrap',
    },
    mfaOff: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 12,
        color: '#FBBF24',
        whiteSpace: 'nowrap',
    },
    mfaUnknown: {
        fontSize: 12,
        color: '#B4C0D3',
        whiteSpace: 'nowrap',
    },
    requireBtn: {
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 6,
        color: '#94A3B8',
        padding: '3px 8px',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
    },
    requireBtnOn: {
        borderColor: 'rgba(34,197,94,0.4)',
        color: '#22C55E',
    },
    removeBtn: {
        background: 'transparent',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 6,
        color: '#EF4444',
        padding: '4px 8px',
        cursor: 'pointer',
        fontSize: 12,
        transition: 'all 0.15s ease',
    },
    confirmRow: {
        display: 'flex',
        gap: 6,
        justifyContent: 'center',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
    actionRow: {
        display: 'flex',
        gap: 6,
        justifyContent: 'center',
        alignItems: 'center',
    },
    editBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 6,
        color: '#94A3B8',
        padding: '4px 8px',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
    },
    editFields: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        flex: 1,
        // The table sizes its columns from their content. Without a real floor
        // here the two text boxes shrink to a few pixels wide and cannot be
        // typed in, so keep a usable minimum for the Member column.
        minWidth: 220,
    },
    editInput: {
        width: '100%',
        minWidth: 0,
        padding: '6px 10px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(59,130,246,0.4)',
        borderRadius: 6,
        color: '#F1F5F9',
        fontSize: 13,
        outline: 'none',
        boxSizing: 'border-box',
    },
    editErrorMsg: {
        fontSize: 12,
        color: '#FCA5A5',
        marginTop: 1,
    },
    saveBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        background: '#3B82F6',
        border: '1px solid #3B82F6',
        borderRadius: 6,
        color: '#fff',
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
    },
    confirmYes: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        background: 'rgba(239,68,68,0.15)',
        border: '1px solid rgba(239,68,68,0.4)',
        borderRadius: 6,
        color: '#FCA5A5',
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: 11,
        fontWeight: 600,
    },
    confirmNo: {
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 6,
        color: '#94A3B8',
        padding: '4px 10px',
        cursor: 'pointer',
        fontSize: 11,
    },
    statusInvited: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        color: '#FB923C', background: 'rgba(251,146,60,0.1)',
        border: '1px solid rgba(251,146,60,0.2)',
    },
    statusAccepted: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        color: '#60A5FA', background: 'rgba(96,165,250,0.1)',
        border: '1px solid rgba(96,165,250,0.2)',
    },
    statusActive: {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
        color: '#22C55E', background: 'rgba(34,197,94,0.08)',
    },
};
